import { useCallback, useRef } from "react";
import { DeviceEventEmitter, Dimensions, Platform } from "react-native";
import { ChatWidget, ChatWidgetRef } from "vdb-ai-chat";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { RedirectFunc } from "@scenes/configurator/OrderCart/utils";
import { useLinkTo, useNavigation } from "@react-navigation/native";
import styled from "styled-components/native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import LineViewContainer from "@scenes/requests/utils/LineView";
import {
  AIEventNames,
  AIEventTypes,
} from "@scenes/authentication/utils/Types/types";
import { useUserAnalytics } from "../../hooks/useAnalytics";
import AiChatHeader from "./ChatHeader";
import { useSelector } from "react-redux";
import { ProductCategory } from "@utils/constants";
import { useSearchFiltersPayload } from "@redux/slices/searchFilters";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { useAIChatBotConfig } from "@redux/slices/globalSlice";

const AIChatBotScreen = () => {
  const { getUserId, getToken, getPriceMode } = useUserInfo();
  const { height } = Dimensions.get("window");
  const linkTo = useLinkTo();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const chatRef = useRef<ChatWidgetRef>(null);
  const { trackEvent } = useUserAnalytics();
  const activeProductType = useSelector(
    (state: any) => state.appState?.activeProduct
  );
  const payload = useSearchFiltersPayload(activeProductType);
  const sectionNameRedux = useSelector(
    (state: any) => state.searchFilters.sectionName
  );
  const aiChatBotConfig = useAIChatBotConfig();
  const getFilterPayload = useCallback(() => {
    let filterPayload = {};
    if (sectionNameRedux === SearchFilterTypeEnum.PAIRS) {
      filterPayload = {
        ...payload?.[sectionNameRedux],
        pair: "pair",
      };
      return filterPayload;
    } else if (sectionNameRedux === SearchFilterTypeEnum.PARCEL) {
      filterPayload = {
        ...payload?.[sectionNameRedux],
        pair: "parcel",
      };
      return filterPayload;
    }
    return payload?.[sectionNameRedux];
  }, [payload, sectionNameRedux]);
  const handleClose = () => {
    navigation.goBack();
    trackEvent(AIEventNames.AI_CHAT_WIDGET, {
      action: AIEventTypes.WIDGET_CLOSED,
    });
  };

  const handleViewAll = (deepLinkUrl: string) => {
    RedirectFunc(deepLinkUrl, linkTo);
    trackEvent(AIEventNames.AI_CHAT_WIDGET, {
      action: AIEventTypes.SHOW_ALL_RESULTS_CLICKED,
      deepLinkUrl: deepLinkUrl,
    });
    DeviceEventEmitter.emit("AI_FILTERS_APPLIED");
  };

  const handleItemPress = (deepLinkUrl: string) => {
    RedirectFunc(deepLinkUrl, linkTo);
    trackEvent(AIEventNames.AI_CHAT_WIDGET, {
      action: AIEventTypes.PRODUCT_PREVIEW_CLICKED,
      deepLinkUrl: deepLinkUrl,
    });
    handleClose();
  };

  const handleClearChat = () => {
    chatRef.current?.clearChat();
    trackEvent(AIEventNames.AI_CHAT_WIDGET, {
      action: AIEventTypes.CHAT_CLEARED,
    });
  };

  const trackAnalyticsEvent = useCallback(
    (eventName: string, eventData: Record<string, any>) => {
      trackEvent(eventName, eventData);
    },
    [trackEvent]
  );

  return (
    <Container style={{ paddingTop: insets.top }}>
      <AiChatHeader
        handleClearChat={handleClearChat}
        handleClose={handleClose}
      />
      <LineViewContainer background={undefined} />
      <ChatContainer>
        <ChatWidget
          ref={chatRef}
          apiUrl={
            Platform.OS === "web" && window.location.hostname !== "localhost"
              ? window.location.protocol + "//" + window.location.host + "/"
              : process.env.EXPO_PUBLIC_API_URL
          }
          theme={{
            primaryColor: "#804195",
            userBubbleColor: "#4F4E57",
            botBubbleColor: "#EDEDF2",
            backgroundColor: "#ffffff",
            fontFamily: "Roboto",
            fontSize: 14,
          }}
          userId={getUserId().toString()}
          userToken={getToken()}
          priceMode={getPriceMode().toString()}
          modalHeight={height * 0.9}
          onViewAllPress={handleViewAll}
          onItemPress={handleItemPress}
          isBetaMode={true}
          activeProductType={
            activeProductType === ProductCategory.LAB_GROWN_DIAMOND
              ? "Lab-Grown"
              : "Natural"
          }
          trackAnalyticsEvent={trackAnalyticsEvent}
          {...(payload?.[sectionNameRedux] && {
            classic_search_filters: getFilterPayload(),
          })}
          enableAiAudioRecording={aiChatBotConfig?.enable_ai_audio_recording}
        />
      </ChatContainer>
    </Container>
  );
};

const Container = styled.View`
  flex: 1;
  background-color: ${({ theme }: { theme: any }) => theme["header-bg-static"]};
`;

const ChatContainer = styled.View`
  flex: 1;
`;

export default AIChatBotScreen;
